<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear
          absolute
          color="success"
          height="4"
          indeterminate
        ></v-progress-linear>
      </template>
      <v-tabs
        v-model="tab"
        class="crm-color"
        left
        dark
        icons-and-text
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab"></div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-form
                @submit.prevent
                ref="requestData"
                v-model="validRequestData"
              >
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.name"
                      :label="$t('GENERAL.LABELS.NAME') + '*'"
                      required
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.company_rank_id"
                      :items="companyRanks"
                      item-text="label"
                      item-value="id"
                      label="Company Rank"
                      :error-messages="validationErrors.company_rank_id || []"
                      clearable
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.company_type_ids"
                      :items="companyTypes"
                      item-text="label"
                      item-value="id"
                      label="Company Types"
                      :error-messages="validationErrors.company_type_ids || []"
                      multiple
                      chips
                      small-chips
                      clearable
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.vat"
                      :label="$t('COMPANIES.VAT')"
                      :error-messages="validationErrors.vat || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.credit_limit"
                      :label="$t('COMPANIES.CREDIT_LIMIT')"
                      type="number"
                      step="0.01"
                      min="0"
                      :error-messages="validationErrors.credit_limit || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.website"
                      :label="$t('COMPANIES.WEBSITE')"
                      :error-messages="validationErrors.website || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.country_id"
                      :items="countries"
                      item-text="label"
                      item-value="id"
                      :label="$t('GENERAL.LABELS.COUNTRY')"
                      :error-messages="validationErrors.country_id || []"
                      clearable
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.owned_by"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      :label="$t('COMPANIES.OWNED_BY')"
                      :error-messages="validationErrors.owned_by || []"
                      clearable
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <FieldTagsInput
                      :value="requestData.information"
                      field-tag-type-slug="company-information"
                      :label="$t('COMPANIES.INFORMATION')"
                      :error-messages="informationErrors()"
                      @input="updateInformation"
                      @tag-added="setChange"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-row no-gutters align="start" class="address-field flex-nowrap">
                      <v-col>
                        <v-textarea
                          v-no-autofill
                          v-model="requestData.address"
                          :label="$t('COMPANIES.ADDRESS')"
                          rows="2"
                          auto-grow
                          :error-messages="validationErrors.address || []"
                          class="address-field__input"
                          @change="setChange"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="auto" class="address-field__button-col">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              class="address-field__button"
                              icon
                              color="primary"
                              v-bind="attrs"
                              v-on="on"
                              @click="showAddressBuilder = true"
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("COMPANIES.ADDRESS_BUILDER") }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-subheader class="px-0 general-emails-title">
                  {{ $t("COMPANIES.GENERAL_EMAILS") }}
                </v-subheader>
                <v-row
                  v-for="(generalEmail, index) in requestData.general_emails"
                  :key="generalEmail._key || index"
                  align="end"
                >
                  <v-col cols="12" md="5">
                    <v-text-field
                      :value="generalEmail.email"
                      :label="`${$t('GENERAL.LABELS.EMAIL')} *`"
                      :error-messages="generalEmailErrors(index, 'email')"
                      @input="updateGeneralEmail(index, 'email', $event)"
                      @change="setChange"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <FieldTagsInput
                      :value="generalEmail.tags"
                      field-tag-type-slug="company-email-types"
                      :label="$t('COMPANIES.EMAIL_TAGS')"
                      :error-messages="generalEmailTagErrors(index)"
                      @input="updateGeneralEmail(index, 'tags', $event)"
                      @tag-added="setChange"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="d-flex align-center">
                    <v-btn
                      icon
                      color="error"
                      :aria-label="$t('GENERAL.LABELS.DELETE')"
                      @click="removeGeneralEmail(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-btn
                  text
                  color="primary"
                  class="mt-0 mb-5"
                  @click="addGeneralEmail"
                >
                  {{ $t("COMPANIES.ADD_GENERAL_EMAIL") }}
                </v-btn>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="requestData.notes"
                      :label="$t('COMPANIES.NOTES')"
                      rows="3"
                      auto-grow
                      :error-messages="validationErrors.notes || []"
                      @change="setChange"
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      class="mt-0"
                      v-model="requestData.active"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      hide-details
                      color="primary"
                      @change="setChange"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-autocomplete
                      v-model="requestData.organization_id"
                      :items="organizations"
                      item-text="label"
                      item-value="id"
                      :label="$t('COMPANIES.REPRESENTS_ORGANIZATION')"
                      :hint="$t('COMPANIES.REPRESENTS_ORGANIZATION_HINT')"
                      persistent-hint
                      clearable
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-form>
              <CompanyAddressesPanel
                class="mt-6"
                :company-id="$route.params.id"
                :countries="countries"
                :can-edit="hasPermissions('companies-edit')"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back(true)">{{ $t("GENERAL.LABELS.BACK_BUTTON") }}</v-btn>
              <v-btn color="success" @click="store()">{{ actionText }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showConfirmSet"
      content="All current changes will be lost."
      @confirm="back"
    ></TPConfirm>
    <AddressBuilderDialog
      v-model="showAddressBuilder"
      :parts="addressBuilderParts"
      :countries="countries"
      @apply="applyBuiltAddress"
    />
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import FieldTagsInput from "@/views/partials/form/FieldTagsInput.vue";
import AddressBuilderDialog, {
  cloneAddressParts,
} from "@/views/pages/companies/partials/AddressBuilderDialog.vue";
import CompanyAddressesPanel from "@/views/pages/companies/partials/CompanyAddressesPanel.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import {
  GET_COMPANY,
  CREATE_COMPANY,
  UPDATE_COMPANY,
  GET_COMPANY_RANKS_OPTIONS,
  GET_COMPANY_TYPES_OPTIONS,
} from "@/store/companies.module";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_COUNTRIES } from "@/store/countries.module";
import { GET_USERS_OPTIONS } from "@/store/users.module";
import { mapGetters } from "vuex";

export default {
  name: "EditCompany",
  components: {
    TPConfirm,
    AddressBuilderDialog,
    FieldTagsInput,
    CompanyAddressesPanel,
  },
  data() {
    return {
      validations,
      generalEmailKeySeq: 0,
      tab: "tab-1",
      isProcessing: false,
      validRequestData: false,
      hasChanges: false,
      showConfirmSet: false,
      showAddressBuilder: false,
      addressBuilderParts: cloneAddressParts(),
      validationErrors: {},
      title: this.$t("COMPANIES.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      companyRanks: [],
      companyTypes: [],
      organizations: [],
      countries: [],
      users: [],
      requestData: {
        name: "",
        company_rank_id: null,
        company_type_ids: [],
        active: true,
        organization_id: null,
        vat: "",
        credit_limit: null,
        country_id: null,
        address: "",
        notes: "",
        owned_by: null,
        website: "",
        information: [],
        general_emails: [],
      },
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
  },
  mounted() {
    Promise.all([
      this.loadCompanyRanks(),
      this.loadCompanyTypes(),
      this.loadOrganizations(),
      this.loadCountries(),
      this.loadUsers(),
    ]).then(() => {
      if (!this.$route.params.id && this.currentUser?.id) {
        this.requestData.owned_by = this.currentUser.id;
      }

      this.selectCompany();
    });
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    setValidationErrors(error) {
      this.validationErrors = (error && error.errors) || {};
    },
    clearValidationError(field) {
      if (!this.validationErrors[field]) {
        return;
      }

      this.$delete(this.validationErrors, field);
    },
    loadCompanyRanks() {
      return this.$store
        .dispatch(GET_COMPANY_RANKS_OPTIONS, {})
        .then((response) => {
          this.companyRanks = response.data || [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    loadCompanyTypes() {
      return this.$store
        .dispatch(GET_COMPANY_TYPES_OPTIONS, {})
        .then((response) => {
          this.companyTypes = response.data || [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    loadOrganizations() {
      return this.$store
        .dispatch(GET_ORGANIZATIONS_OPTIONS, {})
        .then((response) => {
          this.organizations = response.data || [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    loadCountries() {
      return this.$store
        .dispatch(GET_COUNTRIES)
        .then((response) => {
          this.countries = Array.isArray(response) ? response : (response.data || []);
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    loadUsers() {
      return this.$store
        .dispatch(GET_USERS_OPTIONS)
        .then((response) => {
          this.users = response || [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    selectCompany() {
      if (!this.$route.params.id) {
        return;
      }

      this.title = "Update Company";
      this.title = this.$t("COMPANIES.EDIT");
      this.actionText = this.$t("GENERAL.LABELS.EDIT_BUTTON");
      this.isProcessing = true;

      this.$store
        .dispatch(GET_COMPANY, this.$route.params.id)
        .then((response) => {
          if (response.is_system) {
            this.$router.replace({
              name: "companies-view",
              params: { id: this.$route.params.id },
            });
            return;
          }

          this.requestData = {
            name: response.name || "",
            company_rank_id: response.rank?.id || null,
            company_type_ids: Array.isArray(response.types)
              ? response.types.map((type) => type.id).filter(Boolean)
              : [],
            active: !!response.active,
            organization_id: response.organization_id || null,
            vat: response.vat || "",
            credit_limit: response.credit_limit ?? null,
            country_id: response.country_id || null,
            address: response.address || "",
            notes: response.notes || "",
            owned_by: response.owned_by || response.owner?.id || null,
            website: response.website || "",
            information: Array.isArray(response.information)
              ? response.information.map((tag) => ({
                  id: tag.id,
                  value: tag.value,
                }))
              : [],
            general_emails: Array.isArray(response.general_emails)
              ? response.general_emails.map((generalEmail) => ({
                  _key: ++this.generalEmailKeySeq,
                  id: generalEmail.id || null,
                  email: generalEmail.email || "",
                  tags: Array.isArray(generalEmail.tags)
                    ? generalEmail.tags.map((tag) => ({
                        id: tag.id || null,
                        value: tag.value || "",
                      }))
                    : [],
                }))
              : [],
          };
          this.addressBuilderParts = cloneAddressParts({
            street_line_1: response.address || "",
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    setChange() {
      this.hasChanges = true;
      this.clearValidationError("name");
      this.clearValidationError("company_rank_id");
      this.clearValidationError("company_type_ids");
      this.clearInformationValidationErrors();
    },
    updateInformation(information) {
      this.requestData.information = information;
      this.clearInformationValidationErrors();
      this.setChange();
    },
    informationErrors() {
      const messages = Object.keys(this.validationErrors)
        .filter((key) => key.startsWith("information"))
        .flatMap((key) => {
          const err = this.validationErrors[key];
          return Array.isArray(err) ? err : err ? [err] : [];
        });

      return messages;
    },
    clearInformationValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("information")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    applyBuiltAddress({ address, parts }) {
      this.requestData.address = address;
      this.addressBuilderParts = parts;
      this.setChange();
      this.clearValidationError("address");
    },
    addGeneralEmail() {
      this.requestData.general_emails = [
        ...this.requestData.general_emails,
        {
          _key: ++this.generalEmailKeySeq,
          id: null,
          email: "",
          tags: [],
        },
      ];
      this.clearGeneralEmailsValidationErrors();
      this.setChange();
    },
    removeGeneralEmail(index) {
      this.requestData.general_emails = this.requestData.general_emails.filter(
        (_, itemIndex) => itemIndex !== index
      );
      this.clearGeneralEmailsValidationErrors();
      this.setChange();
    },
    updateGeneralEmail(index, field, value) {
      this.requestData.general_emails = this.requestData.general_emails.map(
        (item, itemIndex) => {
          if (itemIndex !== index) {
            return item;
          }

          return {
            ...item,
            [field]: value,
          };
        }
      );
      this.clearGeneralEmailsValidationErrors();
      this.setChange();
    },
    generalEmailErrors(index, field) {
      const key = `general_emails.${index}.${field}`;
      const err = this.validationErrors[key];
      return Array.isArray(err) ? err : err ? [err] : [];
    },
    generalEmailTagErrors(index) {
      const prefix = `general_emails.${index}.tags`;
      const messages = Object.keys(this.validationErrors)
        .filter((key) => key.startsWith(prefix))
        .flatMap((key) => {
          const err = this.validationErrors[key];
          return Array.isArray(err) ? err : err ? [err] : [];
        });

      return messages;
    },
    clearGeneralEmailsValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("general_emails.")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    back(check) {
      if (check && this.hasChanges) {
        this.showConfirmSet = true;
        return;
      }

      this.hasChanges = false;
      this.$router.push({ name: "companies" });
    },
    store() {
      this.$refs.requestData.validate();
      if (!this.validRequestData) {
        return;
      }
      this.validationErrors = {};

      const information = (this.requestData.information || []).map((tag) => ({
        id: tag.id || null,
        value: tag.value,
      }));
      const generalEmails = this.requestData.general_emails.map(({ id, email, tags }) => ({
        id,
        email,
        tags: (tags || []).map((tag) => ({
          id: tag.id || null,
          value: tag.value,
        })),
      }));

      let action = CREATE_COMPANY;
      let payload = {
        ...this.requestData,
        information,
        general_emails: generalEmails,
      };
      if (this.$route.params.id) {
        action = UPDATE_COMPANY;
        payload = {
          id: this.$route.params.id,
          params: {
            ...this.requestData,
            information,
            general_emails: generalEmails,
          },
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, payload)
        .then((response) => {
          const blinkId = response && response.id
            ? response.id
            : this.$route.params.id;

          if (blinkId) {
            userStateService.setState(
              "table-state.companies-table.blinkRowItemKey",
              blinkId
            );
          }

          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.hasChanges = false;
          this.$router.push({ name: "companies" });
        })
        .catch((error) => {
          this.setValidationErrors(error);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};
</script>

<style scoped>
.address-field {
  flex-wrap: nowrap !important;
}

.address-field__input {
  margin-bottom: 0;
  min-width: 0;
}

.address-field__button-col {
  flex: 0 0 auto;
  padding-left: 8px;
}

.address-field__button {
  margin-top: 16px;
}

.general-emails-title {
  font-size: 1.3rem;
}
</style>
